import { debounceTime, first, Subject, Subscription } from 'rxjs';
import { SortOrder } from '../../core/api/api';
import { Plants, PlantsReference } from '../../core/api/api.entities';
import { PlantsApi } from '../../core/api/plants/plants-api.service';
import { getPlantsSelectValue } from '../../shared/utils/plants.utils';
import { SelectPlantsFormControls } from '../form-controls/select-plants.form-controls';
import { SelectPlantsFormOptions } from '../form-options/select-plants.form-options';
import { BaseForm } from './base.form';

export class Select extends BaseForm<SelectPlantsFormControls> {
  formGroup = this.createFormGroup(SelectPlantsFormControls);
  override options = new SelectPlantsFormOptions();

  changeListener?: Subscription;
  loading = true;
  plants?: Plants;
  plantsSelect = new Subject<Plants | undefined>();
  sort = ['seeds.plantName', 'seeds.name', 'seeds.strain'].map(column => ({ column, order: SortOrder.asc }));

  constructor(private plantsApi: PlantsApi) {
    super();

    this.loadData();
  }

  set plantsId(plantsId: number) {
    if (this.options.plants.length) {
      this.plantsSelect.pipe(first()).subscribe(() => {
        this.resetOriginalData();
      });

      this.loadSelectedPlants(plantsId);
    } else {
      setTimeout(() => {
        this.plantsId = plantsId;
      }, 1000);
    }
  }

  clearSelectedPlants(): void {
    this.plants = undefined;
    this.plantsSelect.next(undefined);
    this.controls.plants.setValue(null);
  }

  loadData(): void {
    this.loading = true;

    this.plantsApi.findAllCompact({ alive: true, sort: this.sort }).subscribe(plants => {
      this.options.plants = plants;
      this.setPlantsChangeListener(plants);
      this.loading = false;
    });
  }

  private setPlantsChangeListener(plants: PlantsReference[]): void {
    if (this.changeListener) {
      this.changeListener.unsubscribe();
    }

    this.changeListener = this.controls.plants.valueChanges.pipe(debounceTime(300)).subscribe(name => {
      if (!name) {
        this.options.plants = plants;
      } else {
        this.options.plants = plants.filter(p => Select.isMatchingPlants(p, name));

        const exactMatches = this.options.plants.filter(p => getPlantsSelectValue(p) === name);

        if (exactMatches.length === 1) {
          this.loadSelectedPlants(exactMatches[0].id);
          this.options.plants = [];
        }
      }
    });
  }

  private loadSelectedPlants(plantsId: number): void {
    this.plantsApi.findOne(plantsId).subscribe(plants => {
      this.plants = plants;
      this.plantsSelect.next(plants);
    });
  }

  private static isMatchingPlants(plants: PlantsReference, name: string): boolean {
    return getPlantsSelectValue(plants).toLowerCase().includes(name.toLowerCase());
  }
}
